import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { 
  Calendar, 
  Scissors, 
  Users, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  UserCircle,
  Home
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getMe } from "@/lib/api";

const navItems = [
  { path: "/admin", label: "Calendario", icon: Calendar, exact: true },
  { path: "/admin/servicios", label: "Servicios", icon: Scissors },
  { path: "/admin/peluqueros", label: "Peluqueros", icon: UserCircle },
  { path: "/admin/clientes", label: "Clientes", icon: Users },
  { path: "/admin/configuracion", label: "Configuración", icon: Settings },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await getMe();
      setAdmin(res.data);
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden bg-primary text-primary-foreground p-4 flex items-center justify-between">
        <h1 className="text-xl font-heading">Panel Admin</h1>
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/10"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="btn-menu-toggle"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`admin-sidebar w-64 fixed lg:sticky top-0 h-screen z-40 transition-transform lg:translate-x-0 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <h1 className="text-2xl font-heading">Barbería</h1>
            <p className="text-sm text-white/60 mt-1">Panel de gestión</p>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`admin-sidebar-link ${
                  isActive(item.path, item.exact) ? "active" : ""
                }`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Footer */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
            <Link
              to="/"
              className="admin-sidebar-link mb-2"
              data-testid="nav-home"
            >
              <Home className="h-5 w-5" />
              Ver web pública
            </Link>
            <button
              onClick={handleLogout}
              className="admin-sidebar-link w-full text-red-400 hover:bg-red-500/10"
              data-testid="btn-logout"
            >
              <LogOut className="h-5 w-5" />
              Cerrar sesión
            </button>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen lg:ml-0">
          <div className="p-4 md:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
